import { useState } from 'react';
import {
  AlertTriangle, ArrowDownToLine, ArrowUpFromLine, Battery, FileText, Link2, Loader2, MessageSquare,
  PlusCircle, Repeat, Trash2, WifiOff, Wifi, Cpu,
} from 'lucide-react';
import {
  ENTRADA_LABEL,
  ENTRADAS_BORRABLES,
  ESTADO_LABEL,
  fechaCorta,
  fechaHora,
  GRAVEDAD_LABEL,
  MOTIVO_RETIRADA_LABEL,
  textoUbicacion,
  type EntradaHistorial,
  type TipoEntrada,
} from '@/lib/dispositivos';
import { useCatalogosDispositivos } from './catalogos';

/**
 * El historial de un dispositivo como línea de tiempo. Cada tipo de entrada
 * con su icono y su frase; las incidencias manuales, las pilas y las notas
 * llevan papelera, el resto no: se corrigen con el movimiento contrario.
 */
interface Props {
  entradas: EntradaHistorial[];
  /** Solo estos tipos; sin él, todo. */
  filtro?: TipoEntrada[];
  onBorrar?: (entrada: EntradaHistorial) => Promise<void>;
}

const ICONO: Record<TipoEntrada, React.ElementType> = {
  alta: PlusCircle,
  instalacion: ArrowDownToLine,
  retirada: ArrowUpFromLine,
  estado: Repeat,
  pilas: Battery,
  incidencia: AlertTriangle,
  snapshot_cambio: Cpu,
  enlace: Link2,
  nota: MessageSquare,
  documento: FileText,
};

const COLOR: Record<TipoEntrada, string> = {
  alta: 'text-sereno-dark/50',
  instalacion: 'text-green-600',
  retirada: 'text-amber-600',
  estado: 'text-sky-600',
  pilas: 'text-sereno-dark/60',
  incidencia: 'text-red-600',
  snapshot_cambio: 'text-sereno-dark/50',
  enlace: 'text-sereno-dark/50',
  nota: 'text-sereno-dark/50',
  documento: 'text-sereno-dark/50',
};

export default function LineaTiempo({ entradas, filtro, onBorrar }: Props) {
  const { catalogos } = useCatalogosDispositivos();
  const [ocupado, setOcupado] = useState<string | null>(null);
  const lista = filtro ? entradas.filter((e) => filtro.includes(e.tipo)) : entradas;

  if (lista.length === 0) {
    return <p className="text-sm text-sereno-dark/40 py-3">Todavía no hay nada apuntado.</p>;
  }

  const borrar = async (e: EntradaHistorial) => {
    if (!onBorrar) return;
    if (!confirm('¿Borrar esta entrada? No se puede deshacer.')) return;
    setOcupado(e.id);
    try {
      await onBorrar(e);
    } finally {
      setOcupado(null);
    }
  };

  return (
    <ol className="divide-y divide-gray-50">
      {lista.map((e) => {
        const Icono = e.tipo === 'snapshot_cambio' ? (e.que === 'offline' ? WifiOff : e.que === 'online' ? Wifi : Cpu) : ICONO[e.tipo];
        const borrable = !!onBorrar && ENTRADAS_BORRABLES.includes(e.tipo) && !(e.tipo === 'incidencia' && e.origen === 'auto');
        return (
          <li key={e.id} className="flex items-start gap-3 py-3">
            <Icono size={16} className={`flex-shrink-0 mt-0.5 ${COLOR[e.tipo]}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm">{frase(e, catalogos.incidenciaLabel)}</p>
              {detalle(e) && <p className="text-xs text-sereno-dark/50 mt-0.5 whitespace-pre-wrap">{detalle(e)}</p>}
              <p className="text-[11px] text-sereno-dark/40 mt-0.5">
                {fechaHora(e.fecha)}
                {e.por ? ` · ${e.por}` : ''}
                {e.fecha.slice(0, 16) !== e.registradoEn.slice(0, 16) ? ` · apuntado el ${fechaCorta(e.registradoEn)}` : ''}
              </p>
            </div>
            {borrable && (
              <button
                type="button"
                onClick={() => void borrar(e)}
                disabled={ocupado === e.id}
                className="p-1.5 text-sereno-dark/30 hover:text-red-600 transition-colors disabled:opacity-50 flex-shrink-0"
                title="Borrar"
              >
                {ocupado === e.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function frase(e: EntradaHistorial, incidenciaLabel: Record<string, string>): string {
  switch (e.tipo) {
    case 'alta':
      return e.origen === 'importacion' ? 'Alta por sincronización con la cuenta' : 'Alta manual';
    case 'instalacion':
      return `Instalado en ${e.nickname}${e.principal ? '' : ' (además de su casa principal)'}${e.rol === 'smartlock2' ? ', como segunda smartlock' : ''}`;
    case 'retirada':
      return `Retirado de ${e.nickname} · ${MOTIVO_RETIRADA_LABEL[e.motivo] ?? e.motivo}`;
    case 'estado':
      return e.de === e.a ? `Cambia de sitio: ${textoUbicacion(e.ubicacion)}` : `${ESTADO_LABEL[e.de]} → ${ESTADO_LABEL[e.a]}`;
    case 'pilas':
      return 'Pilas cambiadas';
    case 'incidencia':
      return `${incidenciaLabel[e.incidenciaTipo] ?? e.incidenciaTipo}${e.gravedad === 'bloquea_acceso' ? ` · ${GRAVEDAD_LABEL[e.gravedad]}` : ''}${e.ocurrencias && e.ocurrencias > 1 ? ` ×${e.ocurrencias}` : ''}${e.origen === 'auto' ? ' · automática' : ''}`;
    case 'snapshot_cambio':
      switch (e.que) {
        case 'offline': return 'Pasa a offline';
        case 'online': return 'Vuelve a estar online';
        case 'bateria_critica': return 'La batería pasa a crítica';
        case 'bateria_ok': return 'La batería vuelve a estar bien';
        case 'firmware': return `Firmware ${e.de ?? '?'} → ${e.a ?? '?'}`;
      }
      return ENTRADA_LABEL[e.tipo];
    case 'enlace':
      return e.enlazadoA ? `Enlazado a ${e.enlazadoA}${e.antes ? ` (antes ${e.antes})` : ''}` : `Desenlazado de ${e.antes ?? '?'}`;
    case 'nota':
      return 'Nota';
    case 'documento':
      return e.accion === 'subido' ? `Documento subido: ${e.fileName}` : `Documento borrado: ${e.fileName}`;
  }
}

function detalle(e: EntradaHistorial): string | null {
  switch (e.tipo) {
    case 'alta':
    case 'pilas':
      return e.nota ?? null;
    case 'instalacion':
    case 'retirada': {
      const partes: string[] = [];
      if (e.hostify && !e.hostify.escrito) partes.push(`Hostify (campo ${e.hostify.campo ?? '?'}): ${e.hostify.error ?? 'no escrito'}`);
      if (e.nota) partes.push(e.nota);
      return partes.join('\n') || null;
    }
    case 'estado': {
      const partes: string[] = [];
      if (e.de !== e.a && e.ubicacion) partes.push(textoUbicacion(e.ubicacion));
      if (e.reparacion) partes.push(`${e.reparacion.proveedor}, enviado el ${fechaCorta(e.reparacion.enviadoEl)}${e.reparacion.referencia ? ` · ${e.reparacion.referencia}` : ''}`);
      if (e.nota) partes.push(e.nota);
      return partes.join(' · ') || null;
    }
    case 'incidencia':
      return [e.descripcion, e.queSeHizo ? `Qué se hizo: ${e.queSeHizo}` : ''].filter(Boolean).join('\n');
    case 'nota':
      return e.texto;
    default:
      return null;
  }
}
